import { useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Copy } from "lucide-react";
import { actionDefaults, ruleDefaults } from "./defaults";
import { objectEvents } from "./runtime";
import type {
  Action,
  Condition,
  Rule,
  Scene,
  SceneContent,
  Value,
} from "./types";
const eventLabels: Record<Rule["event"], string> = {
  start: "Начало сцены",
  click: "Клик по объекту",
  press: "Нажатие на объект",
  hover: "Наведение на объект",
  leave: "Курсор ушёл с объекта",
  key: "Нажатие клавиши",
  timer: "Задержка / таймер",
  trigger: "Вход в триггер",
  pickup: "Подбор предмета",
  interact: "Взаимодействие · E",
  animationEnd: "Анимация завершилась",
  stateChange: "Состояние сменилось",
  variable: "Переменная изменилась",
};
const transitions = {
  instant: "Мгновенно",
  fade: "Растворение",
  slide: "Сдвиг",
};
const targetedActions = [
  "show",
  "hide",
  "activate",
  "deactivate",
  "destroy",
  "text",
  "state",
  "play",
  "stop",
];
const actions = {
  set: "Задать переменную",
  add: "Увеличить переменную",
  subtract: "Уменьшить переменную",
  show: "Показать объект",
  hide: "Скрыть объект",
  activate: "Активировать объект",
  deactivate: "Отключить объект",
  destroy: "Удалить объект",
  text: "Изменить текст",
  state: "Сменить состояние",
  play: "Проиграть анимацию",
  stop: "Остановить анимацию",
  frame: "Перейти к фрейму",
  scene: "Перейти в сцену",
  win: "Победа",
  lose: "Поражение",
};
function ValueInput({
  type,
  value,
  onChange,
  disabled,
}: {
  type: string;
  value: Value;
  onChange: (v: Value) => void;
  disabled: boolean;
}) {
  return type === "boolean" ? (
    <input
      aria-label="Значение"
      type="checkbox"
      checked={value === true}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
  ) : (
    <input
      aria-label="Значение"
      type={type === "number" ? "number" : "text"}
      step="any"
      value={String(value)}
      disabled={disabled}
      onChange={(e) =>
        onChange(type === "number" ? Number(e.target.value) : e.target.value)
      }
    />
  );
}
export function LogicPanel({
  content,
  change,
  readOnly,
  scenes,
  live,
  logs,
}: {
  content: SceneContent;
  change: (c: SceneContent) => void;
  readOnly: boolean;
  scenes: Scene[];
  live: Record<string, Value> | null;
  logs: string[];
}) {
  const [tab, setTab] = useState("rules"),
    [selected, setSelected] = useState<string | null>(
      content.rules[0]?.id || null,
    );
  const rule = content.rules.find((r) => r.id === selected) || content.rules[0],
    vars = content.variables,
    available = [
      ...new Map(
        [
          ...scenes.flatMap((s) =>
            s.content.variables.filter((v) => v.scope === "project"),
          ),
          ...vars,
        ].map((v) => [v.name, v]),
      ).values(),
    ];
  const patch = (v: Partial<Rule>) => {
    if (rule)
      change({
        ...content,
        rules: content.rules.map((r) =>
          r.id === rule.id ? { ...r, ...v } : r,
        ),
      });
  };
  const objectOptions = (event = false) => (
    <>
      <option value="">Выбери объект</option>
      <option value={event ? "$any" : "$event"}>
        {event ? "Любой подходящий объект" : "Объект события"}
      </option>
      {content.objects.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </>
  );
  // States are picked by name: for «объект события» any name used in the
  // scene is offered, since the object is only known while playing.
  const stateOptions = (
    targetId: string,
    current: Value,
    empty = "Исходная картинка",
  ) => {
    const names = [
      ...new Set(
        content.objects
          .filter((o) =>
            ["$event", "$any"].includes(targetId) ? true : o.id === targetId,
          )
          .flatMap((o) => o.states.map((s) => s.name)),
      ),
    ];
    if (String(current) && !names.includes(String(current)))
      names.push(String(current));
    return (
      <>
        <option value="">{empty}</option>
        {names.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </>
    );
  };
  const animationOptions = (
    targetId: string,
    current: Value,
    empty = "Выбери анимацию",
  ) => {
    const names = [
      ...new Set(
        content.objects
          .filter((o) =>
            ["$event", "$any"].includes(targetId) ? true : o.id === targetId,
          )
          .flatMap((o) => o.animations.map((a) => a.name)),
      ),
    ];
    if (String(current) && !names.includes(String(current)))
      names.push(String(current));
    return (
      <>
        <option value="">{empty}</option>
        {names.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </>
    );
  };
  const varOptions = (empty = "Выбери переменную") => (
    <>
      <option value="">{empty}</option>
      {available.map((v) => (
        <option key={v.name} value={v.name}>
          {v.name}
        </option>
      ))}
    </>
  );
  const frames = content.objects.filter((o) => o.kind === "frame");
  const conditionPatch = (index: number, values: Partial<Condition>) =>
    patch({
      conditions: rule!.conditions.map((c, i) =>
        i === index ? { ...c, ...values } : c,
      ),
    });
  const actionSection = (
    r: Rule,
    key: "actions" | "elseActions",
    title: string,
  ) => {
    const list = r[key] ?? [];
    const actionPatch = (index: number, values: Partial<Action>) =>
      patch({
        [key]: list.map((a, i) => (i === index ? { ...a, ...values } : a)),
      });
    // The ELSE list repeats every control, so its labels say which list.
    const L = (label: string) =>
      key === "elseActions" ? "Иначе: " + label.toLowerCase() : label;
    return (
      <div className="rule-section">
        <div className="logic-section-title">
          <span className="rule-label">{title}</span>
          <button
            className="text-button"
            aria-label={L("Действие")}
            disabled={readOnly}
            onClick={() =>
              patch({ [key]: [...list, actionDefaults()] })
            }
          >
            <Plus size={13} />
            Действие
          </button>
        </div>
        {list.map((a, i) => (
          <div className="rule-row" key={i}>
            <select
              aria-label={L("Тип действия")}
              disabled={readOnly}
              value={a.type}
              onChange={(e) => {
                const type = e.target.value as Action["type"],
                  stateful =
                    type === "state"
                      ? content.objects.find((o) => o.states.length)
                      : undefined,
                  animated = ["play", "stop"].includes(type)
                    ? content.objects.find((o) => o.animations.length)
                    : undefined;
                actionPatch(i, {
                  ...actionDefaults(type),
                  variable: available[0]?.name || "",
                  targetId:
                    type === "frame"
                      ? (frames[0]?.id ?? "")
                      : stateful?.id ||
                        animated?.id ||
                        content.objects[0]?.id ||
                        "$event",
                  ...(stateful && { value: stateful.states[0].name }),
                  ...(animated &&
                    type === "play" && {
                      value: animated.animations[0].name,
                    }),
                });
              }}
            >
              {Object.entries(actions).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            {["set", "add", "subtract"].includes(a.type) && (
              <>
                <select
                  aria-label={L("Переменная действия")}
                  value={a.variable}
                  disabled={readOnly}
                  onChange={(e) =>
                    actionPatch(i, {
                      variable: e.target.value,
                      value:
                        available.find((v) => v.name === e.target.value)
                          ?.value ?? 0,
                    })
                  }
                >
                  {varOptions()}
                </select>
                <ValueInput
                  type={
                    available.find((v) => v.name === a.variable)
                      ?.type || "number"
                  }
                  value={a.value}
                  disabled={readOnly}
                  onChange={(value) => actionPatch(i, { value })}
                />
              </>
            )}
            {targetedActions.includes(a.type) && (
              <>
                <select
                  aria-label={L("Объект действия")}
                  value={a.targetId}
                  disabled={readOnly}
                  onChange={(e) =>
                    actionPatch(i, { targetId: e.target.value })
                  }
                >
                  {objectOptions()}
                </select>
                {(a.targetIds ?? []).map((id, j) => (
                  <span className="extra-target" key={j}>
                    <select
                      aria-label={L(`Ещё объект ${j + 1}`)}
                      value={id}
                      disabled={readOnly}
                      onChange={(e) =>
                        actionPatch(i, {
                          targetIds: a.targetIds.map((v, k) =>
                            k === j ? e.target.value : v,
                          ),
                        })
                      }
                    >
                      {objectOptions()}
                    </select>
                    <button
                      className="icon-button"
                      aria-label={L(`Убрать объект ${j + 1}`)}
                      disabled={readOnly}
                      onClick={() =>
                        actionPatch(i, {
                          targetIds: a.targetIds.filter((_, k) => k !== j),
                        })
                      }
                    >
                      <Trash2 size={12} />
                    </button>
                  </span>
                ))}
                <button
                  className="icon-button"
                  aria-label={L("Добавить объект в действие")}
                  title="Применить к нескольким объектам"
                  disabled={readOnly || (a.targetIds ?? []).length >= 20}
                  onClick={() =>
                    actionPatch(i, {
                      targetIds: [
                        ...(a.targetIds ?? []),
                        content.objects.find(
                          (o) =>
                            o.id !== a.targetId &&
                            !a.targetIds?.includes(o.id),
                        )?.id ?? "",
                      ],
                    })
                  }
                >
                  <Plus size={12} />
                </button>
              </>
            )}
            {a.type === "frame" && (
              <>
                <select
                  aria-label={L("Фрейм")}
                  value={a.targetId}
                  disabled={readOnly}
                  onChange={(e) =>
                    actionPatch(i, { targetId: e.target.value })
                  }
                >
                  <option value="">Выбери фрейм</option>
                  {frames.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <select
                  aria-label={L("Переход")}
                  value={String(a.value)}
                  disabled={readOnly}
                  onChange={(e) =>
                    actionPatch(i, { value: e.target.value })
                  }
                >
                  {Object.entries(transitions).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </>
            )}
            {a.type === "state" && (
              <select
                aria-label={L("Новое состояние")}
                value={String(a.value)}
                disabled={readOnly}
                onChange={(e) =>
                  actionPatch(i, { value: e.target.value })
                }
              >
                {stateOptions(a.targetId, a.value)}
              </select>
            )}
            {a.type === "play" && (
              <select
                aria-label={L("Анимация")}
                value={String(a.value)}
                disabled={readOnly}
                onChange={(e) =>
                  actionPatch(i, { value: e.target.value })
                }
              >
                {animationOptions(a.targetId, a.value)}
              </select>
            )}
            {["text", "win", "lose"].includes(a.type) && (
              <ValueInput
                type="text"
                value={a.value}
                disabled={readOnly}
                onChange={(value) => actionPatch(i, { value })}
              />
            )}
            {a.type === "scene" && (
              <select
                aria-label={L("Сцена перехода")}
                value={a.sceneId}
                disabled={readOnly}
                onChange={(e) =>
                  actionPatch(i, { sceneId: e.target.value })
                }
              >
                <option value="">Выбери сцену</option>
                {scenes.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
            )}
            <button
              className="icon-button"
              aria-label={L("Поднять действие")}
              disabled={readOnly || i === 0}
              onClick={() => {
                const next = [...list];
                [next[i - 1], next[i]] = [next[i], next[i - 1]];
                patch({ [key]: next });
              }}
            >
              <ChevronUp size={12} />
            </button>
            <button
              className="icon-button"
              aria-label={L("Опустить действие")}
              disabled={readOnly || i === list.length - 1}
              onClick={() => {
                const next = [...list];
                [next[i + 1], next[i]] = [next[i], next[i + 1]];
                patch({ [key]: next });
              }}
            >
              <ChevronDown size={12} />
            </button>
            <button
              className="icon-button"
              aria-label={L("Удалить действие")}
              disabled={readOnly}
              onClick={() =>
                patch({
                  [key]: list.filter((_, j) => i !== j),
                })
              }
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    );
  };
  return (
    <section className="logic-panel">
      <div className="logic-tabs">
        <button aria-pressed={tab === "rules"} onClick={() => setTab("rules")}>
          Правила <span>{content.rules.length}</span>
        </button>
        <button
          aria-pressed={tab === "variables"}
          onClick={() => setTab("variables")}
        >
          Переменные
        </button>
        <button aria-pressed={tab === "log"} onClick={() => setTab("log")}>
          Журнал
        </button>
      </div>
      {tab === "variables" ? (
        <div className="variables-editor">
          <div className="logic-section-title">
            <span>
              Начальные значения{live ? " · текущие значения справа" : ""}
            </span>
            <button
              className="text-button"
              disabled={readOnly}
              onClick={() => {
                let i = vars.length + 1;
                while (vars.some((v) => v.name === `var${i}`)) i++;
                change({
                  ...content,
                  variables: [
                    ...vars,
                    {
                      name: `var${i}`,
                      type: "number",
                      scope: "scene",
                      value: 0,
                    },
                  ],
                });
              }}
            >
              <Plus size={14} />
              Переменная
            </button>
          </div>
          {vars.map((v, i) => (
            <div className="variable-row" key={i}>
              <input
                aria-label="Имя переменной"
                value={v.name}
                disabled={readOnly}
                maxLength={40}
                onChange={(e) => {
                  const name = e.target.value;
                  change({
                    ...content,
                    variables: vars.map((n, j) =>
                      j === i ? { ...n, name } : n,
                    ),
                    rules: content.rules.map((r) => ({
                      ...r,
                      conditions: r.conditions.map((c) =>
                        c.variable === v.name ? { ...c, variable: name } : c,
                      ),
                      actions: r.actions.map((a) =>
                        a.variable === v.name ? { ...a, variable: name } : a,
                      ),
                    })),
                  });
                }}
              />
              <select
                aria-label="Тип переменной"
                value={v.type}
                disabled={readOnly}
                onChange={(e) =>
                  change({
                    ...content,
                    variables: vars.map((n, j) =>
                      j === i
                        ? {
                            ...n,
                            type: e.target.value as typeof v.type,
                            value:
                              e.target.value === "number"
                                ? 0
                                : e.target.value === "boolean"
                                  ? false
                                  : "",
                          }
                        : n,
                    ),
                  })
                }
              >
                <option value="number">Число</option>
                <option value="text">Текст</option>
                <option value="boolean">Переключатель</option>
              </select>
              <select
                aria-label="Область переменной"
                value={v.scope}
                disabled={readOnly}
                onChange={(e) =>
                  change({
                    ...content,
                    variables: vars.map((n, j) =>
                      j === i
                        ? { ...n, scope: e.target.value as typeof v.scope }
                        : n,
                    ),
                  })
                }
              >
                <option value="scene">В сцене</option>
                <option value="project">Во всём проекте</option>
              </select>
              <ValueInput
                type={v.type}
                value={v.value}
                disabled={readOnly}
                onChange={(value) =>
                  change({
                    ...content,
                    variables: vars.map((n, j) =>
                      j === i ? { ...n, value } : n,
                    ),
                  })
                }
              />
              {live && <code>{String(live[v.name])}</code>}
              <button
                className="icon-button"
                disabled={readOnly}
                aria-label={`Удалить переменную ${v.name}`}
                onClick={() =>
                  change({
                    ...content,
                    variables: vars.filter((_, j) => i !== j),
                  })
                }
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          {!vars.length && (
            <p className="small-text muted">
              Добавь счётчики, ресурсы или флаги состояния.
            </p>
          )}
        </div>
      ) : tab === "log" ? (
        <div className="game-log">
          {logs.length ? (
            logs
              .slice()
              .reverse()
              .map((s, i) => <div key={i}>{s}</div>)
          ) : (
            <p className="muted small-text">
              События появятся после запуска сцены.
            </p>
          )}
        </div>
      ) : (
        <div className="rules-layout">
          <div className="rules-list">
            {content.rules.map((r) => (
              <button
                key={r.id}
                aria-pressed={r.id === rule?.id}
                onClick={() => setSelected(r.id)}
              >
                <strong>{r.name}</strong>
                <span>{r.enabled ? eventLabels[r.event] : "Отключено"}</span>
              </button>
            ))}
            <button
              className="text-button"
              disabled={readOnly}
              onClick={() => {
                const r = ruleDefaults(
                  content.objects.find((o) => o.interactable)?.id || "",
                );
                change({ ...content, rules: [...content.rules, r] });
                setSelected(r.id);
              }}
            >
              <Plus size={14} />
              Правило
            </button>
          </div>
          {rule ? (
            <div className="rule-editor">
              <div className="rule-header">
                <button
                  className="icon-button"
                  aria-label="Дублировать правило"
                  disabled={readOnly}
                  onClick={() => {
                    const copy = {
                      ...structuredClone(rule),
                      id: crypto.randomUUID(),
                      name: rule.name.slice(0, 145) + " — копия",
                    };
                    change({ ...content, rules: [...content.rules, copy] });
                    setSelected(copy.id);
                  }}
                >
                  <Copy size={14} />
                </button>
                <input
                  aria-label="Название правила"
                  value={rule.name}
                  maxLength={160}
                  disabled={readOnly}
                  onChange={(e) => patch({ name: e.target.value })}
                />
                <label className="game-check">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={rule.enabled}
                    onChange={(e) => patch({ enabled: e.target.checked })}
                  />
                  Включено
                </label>
                <button
                  className="icon-button"
                  aria-label="Удалить правило"
                  disabled={readOnly}
                  onClick={() =>
                    change({
                      ...content,
                      rules: content.rules.filter((r) => r.id !== rule.id),
                    })
                  }
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="rule-section">
                <span className="rule-label">КОГДА</span>
                <select
                  aria-label="Событие правила"
                  disabled={readOnly}
                  value={rule.event}
                  onChange={(e) => {
                    const event = e.target.value as Rule["event"];
                    patch({
                      event,
                      detail: "",
                      targetId:
                        objectEvents.includes(event) && !rule.targetId
                          ? "$any"
                          : rule.targetId,
                    });
                  }}
                >
                  {Object.entries(eventLabels).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
                {objectEvents.includes(rule.event) && (
                  <select
                    aria-label="Объект события"
                    disabled={readOnly}
                    value={rule.targetId}
                    onChange={(e) => patch({ targetId: e.target.value })}
                  >
                    {objectOptions(true)}
                  </select>
                )}
                {rule.event === "animationEnd" && (
                  <select
                    aria-label="Какая анимация"
                    disabled={readOnly}
                    value={rule.detail}
                    onChange={(e) => patch({ detail: e.target.value })}
                  >
                    {animationOptions(rule.targetId, rule.detail, "Любая анимация")}
                  </select>
                )}
                {rule.event === "stateChange" && (
                  <select
                    aria-label="Какое состояние"
                    disabled={readOnly}
                    value={rule.detail}
                    onChange={(e) => patch({ detail: e.target.value })}
                  >
                    {stateOptions(rule.targetId, rule.detail, "Любое состояние")}
                  </select>
                )}
                {rule.event === "variable" && (
                  <select
                    aria-label="Какая переменная"
                    disabled={readOnly}
                    value={rule.detail}
                    onChange={(e) => patch({ detail: e.target.value })}
                  >
                    {varOptions("Любая переменная")}
                  </select>
                )}
                {rule.event === "key" && (
                  <select
                    aria-label="Клавиша события"
                    disabled={readOnly}
                    value={rule.key}
                    onChange={(e) => patch({ key: e.target.value })}
                  >
                    {[
                      "KeyE",
                      "Space",
                      "KeyW",
                      "KeyA",
                      "KeyS",
                      "KeyD",
                      "ArrowUp",
                      "ArrowDown",
                      "ArrowLeft",
                      "ArrowRight",
                    ].map((code) => (
                      <option key={code}>{code}</option>
                    ))}
                  </select>
                )}
                {rule.event === "timer" && (
                  <>
                    <input
                      type="number"
                      aria-label="Таймер, секунд"
                      min={0.1}
                      max={3600}
                      step={0.1}
                      value={rule.seconds}
                      disabled={readOnly}
                      onChange={(e) =>
                        patch({
                          seconds: Math.max(0.1, Number(e.target.value)),
                        })
                      }
                    />
                    <label className="game-check">
                      <input
                        type="checkbox"
                        disabled={readOnly}
                        checked={rule.repeat}
                        onChange={(e) => patch({ repeat: e.target.checked })}
                      />
                      Повторять
                    </label>
                  </>
                )}
              </div>
              <div className="rule-section">
                <div className="logic-section-title">
                  <span className="rule-label">ЕСЛИ · ВСЕ УСЛОВИЯ</span>
                  <button
                    className="text-button"
                    disabled={readOnly}
                    onClick={() =>
                      patch({
                        conditions: [
                          ...rule.conditions,
                          {
                            kind: "variable",
                            variable: available[0]?.name || "",
                            targetId: "",
                            operator: "eq",
                            value: available[0]?.value ?? 0,
                            compare: "value",
                            other: "",
                          },
                        ],
                      })
                    }
                  >
                    <Plus size={13} />
                    Условие
                  </button>
                </div>
                {rule.conditions.map((c, i) => (
                  <div className="rule-row" key={i}>
                    <select
                      aria-label="Тип условия"
                      disabled={readOnly}
                      value={c.kind}
                      onChange={(e) =>
                        conditionPatch(i, {
                          kind: e.target.value as Condition["kind"],
                          operator: "eq",
                          value:
                            e.target.value === "active"
                              ? true
                              : e.target.value === "state"
                                ? ""
                                : 0,
                        })
                      }
                    >
                      <option value="variable">Переменная</option>
                      <option value="active">Объект активен</option>
                      <option value="state">Объект в состоянии</option>
                    </select>
                    <select
                      aria-label="Цель условия"
                      disabled={readOnly}
                      value={c.kind === "variable" ? c.variable : c.targetId}
                      onChange={(e) =>
                        conditionPatch(
                          i,
                          c.kind === "variable"
                            ? {
                                variable: e.target.value,
                                value:
                                  available.find(
                                    (v) => v.name === e.target.value,
                                  )?.value ?? 0,
                              }
                            : { targetId: e.target.value },
                        )
                      }
                    >
                      {c.kind === "variable" ? varOptions() : objectOptions()}
                    </select>
                    <select
                      aria-label="Сравнение"
                      disabled={readOnly}
                      value={c.operator}
                      onChange={(e) =>
                        conditionPatch(i, {
                          operator: e.target.value as Condition["operator"],
                        })
                      }
                    >
                      {Object.entries({
                        eq: "=",
                        ne: "≠",
                        gt: ">",
                        gte: "≥",
                        lt: "<",
                        lte: "≤",
                      })
                        .filter(
                          ([key]) =>
                            c.kind !== "state" || ["eq", "ne"].includes(key),
                        )
                        .map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                    </select>
                    {c.kind === "variable" && (
                      <select
                        aria-label="Сравнить с"
                        disabled={readOnly}
                        value={c.compare ?? "value"}
                        onChange={(e) =>
                          conditionPatch(i, {
                            compare: e.target.value as Condition["compare"],
                            other:
                              available.find((v) => v.name !== c.variable)
                                ?.name ?? "",
                          })
                        }
                      >
                        <option value="value">значением</option>
                        <option value="variable">переменной</option>
                      </select>
                    )}
                    {c.kind === "variable" && c.compare === "variable" ? (
                      <select
                        aria-label="Вторая переменная"
                        disabled={readOnly}
                        value={c.other}
                        onChange={(e) =>
                          conditionPatch(i, { other: e.target.value })
                        }
                      >
                        {varOptions()}
                      </select>
                    ) : c.kind === "state" ? (
                      <select
                        aria-label="Состояние условия"
                        disabled={readOnly}
                        value={String(c.value)}
                        onChange={(e) =>
                          conditionPatch(i, { value: e.target.value })
                        }
                      >
                        {stateOptions(c.targetId, c.value)}
                      </select>
                    ) : (
                      <ValueInput
                        type={
                          c.kind === "active"
                            ? "boolean"
                            : available.find((v) => v.name === c.variable)
                                ?.type || "number"
                        }
                        value={c.value}
                        disabled={readOnly}
                        onChange={(value) => conditionPatch(i, { value })}
                      />
                    )}
                    <button
                      className="icon-button"
                      aria-label="Удалить условие"
                      disabled={readOnly}
                      onClick={() =>
                        patch({
                          conditions: rule.conditions.filter((_, j) => i !== j),
                        })
                      }
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
              {actionSection(rule, "actions", "СДЕЛАТЬ · ПО ПОРЯДКУ")}
              {(rule.conditions.length > 0 || rule.elseActions?.length > 0) &&
                actionSection(rule, "elseActions", "ИНАЧЕ")}
            </div>
          ) : (
            <p className="muted small-text">
              Создай правило, чтобы описать реакцию игры на действия игрока.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
